import type {
  AssessmentListRow,
  AssessmentStatus,
  CreateAssessment,
  Effect,
  FlowRouteTaken,
  FlowStep,
  FlowTrace,
  JourneyRuntime,
  Page,
  SubmitResponses,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import type { QuestionsView } from './flow';
import { refreshLists } from '../../lib/list';

export interface AssessmentSummary {
  id: string;
  title: string;
  customer_key: string;
  customer_name: string;
  context: {
    country: string | null;
    industry: string | null;
    product: string | null;
    customer: string | null;
  };
  bundle_id: string;
  journey_key: string | null;
  status: AssessmentStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  submitted_by: string | null;
  /** The IT environment of a group it covers (task 94). */
  environment: string | null;
  entities: string[];
}
export interface AssessmentDetail extends AssessmentSummary {
  participants: { user_id: string; display_name: string | null; email: string | null; added_at: string }[];
}
export interface Candidate {
  id: string;
  name: string | null;
  email: string | null;
  roles: string[];
}
export interface AnswersResult {
  recorded: { question: string; response_id: string; cleared: boolean; effects: Effect[] }[];
  unchanged: string[];
  now_hidden: string[];
  journey: JourneyRuntime | null;
  stage_changes: { stage: string; from: string | null; to: string }[];
}

/** People who can start assessments and add others to them. */

export function useCanStart() {
  const { tenant } = useSession();
  return can(tenant, 'assessments.create');
}

/** People who answer (the core's ANSWERERS): they upload documents and decide what documents say. */

export function useCanAnswer() {
  const { tenant } = useSession();
  return can(tenant, 'assessments.edit');
}

/** People who can hand a finished assessment to the insurer (the core's SUBMITTERS). */

export function useCanSubmit() {
  const { tenant } = useSession();
  return can(tenant, 'assessments.submit');
}

const keys = {
  all: (t?: string) => ['assessments', t] as const,
  one: (t: string | undefined, id: string) => ['assessments', t, id] as const,
};

/**
 * The most recently active assessments, for pickers and the business owner's own few. Long lists page
 * through /assessments with the list view instead (task 60).
 */
export function useAssessments() {
  const { tenant } = useSession();
  return useQuery({
    queryKey: keys.all(tenant?.id),
    queryFn: () => api<Page<AssessmentListRow>>('GET', '/assessments?page_size=96').then((p) => p.items),
    enabled: !!tenant && tenant.kind !== 'platform',
  });
}

export function useAssessment(id: string, opts: { enabled?: boolean } = {}) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...keys.one(tenant?.id, id), 'detail'],
    queryFn: () => api<AssessmentDetail>('GET', `/assessments/${id}`),
    enabled: !!tenant && (opts.enabled ?? true),
  });
}

export function useQuestions(id: string) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...keys.one(tenant?.id, id), 'questions'],
    queryFn: () => api<QuestionsView>('GET', `/assessments/${id}/questions`),
    enabled: !!tenant,
  });
}

export function useJourney(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...keys.one(tenant?.id, id), 'journey'],
    queryFn: () => api<JourneyRuntime>('GET', `/assessments/${id}/journey`),
    enabled: !!tenant && enabled,
  });
}

/** The path the assessment took (task 85): what each route skipped, and why. */
export interface AssessmentFlow extends Omit<FlowTrace, 'taken' | 'steps'> {
  taken: (FlowRouteTaken & { from_text: string; answer: string })[];
  steps: (FlowStep & { text: string })[];
}

export function useFlow(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...keys.one(tenant?.id, id), 'flow'],
    queryFn: () => api<AssessmentFlow>('GET', `/assessments/${id}/flow`),
    enabled: !!tenant && enabled,
  });
}

export function useCandidates(id: string, enabled: boolean) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...keys.one(tenant?.id, id), 'candidates'],
    queryFn: () => api<Candidate[]>('GET', `/assessments/${id}/candidates`),
    enabled: !!tenant && enabled,
  });
}

export function useCreateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssessment) => api<AssessmentSummary>('POST', '/assessments', body),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['assessments'] }), refreshLists(qc)]),
  });
}

export function useAddParticipant(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (userId: string) => api('POST', `/assessments/${id}/participants`, { user_id: userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.one(tenant?.id, id) }),
  });
}

/**
 * Records answers from the form. Whatever happens, the questions are reloaded (a 409 means they changed),
 * and the mutation settles only once they are, so the next question is picked from fresh data.
 */
export function useAnswer(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (answers: SubmitResponses['answers']) =>
      api<AnswersResult>('POST', `/assessments/${id}/responses`, { channel: 'form', answers }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.one(tenant?.id, id) }),
  });
}

/** Hands the assessment to the insurer; answers stop. Everything about it (and the portfolio) reloads. */
export function useSubmitAssessment(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: () => api<AssessmentSummary>('POST', `/assessments/${id}/submit`),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: keys.all(tenant?.id) }),
        qc.invalidateQueries({ queryKey: ['portfolio'] }),
      ]),
  });
}
